/**
 * api/contracts.js — frontend client for the Procurement Contracts module.
 *
 * Convention (§17.2.f): every module gets its own api/<module>.js file.
 * No fetch() calls live in pages.
 */
import api from '../lib/api';

const base = '/procurement';

export const COMMODITIES = [
  { value: 'electricity', label: 'Electricity' },
  { value: 'gas', label: 'Gas' },
  { value: 'water', label: 'Water' },
  { value: 'sewer', label: 'Sewer' },
  { value: 'trash', label: 'Trash' },
  { value: 'dual', label: 'Dual (Gas + Electric)' },
  { value: 'heating_oil', label: 'Heating Oil' },
  { value: 'propane', label: 'Propane' },
];

export const CONTRACT_TYPES = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'index', label: 'Index' },
  { value: 'block_index', label: 'Block & Index' },
];

export const listContracts = ({ accountId, ...params } = {}) =>
  api.get(`${base}/contracts`, { params: { account_id: accountId, ...params } }).then((r) => r.data);

export const createContract = ({ accountId, payload }) =>
  api.post(`${base}/contracts`, payload, { params: { account_id: accountId } }).then((r) => r.data);

export const getContract = ({ accountId, id }) =>
  api.get(`${base}/contracts/${id}`, { params: { account_id: accountId } }).then((r) => r.data);

export const patchContract = ({ accountId, id, patch }) =>
  api.patch(`${base}/contracts/${id}`, patch, { params: { account_id: accountId } }).then((r) => r.data);

export const deleteContract = ({ accountId, id }) =>
  api.delete(`${base}/contracts/${id}`, { params: { account_id: accountId } }).then((r) => r.data);

export const getPerformance = ({ accountId, id }) =>
  api.get(`${base}/contracts/${id}/performance`, { params: { account_id: accountId } }).then((r) => r.data);

export const searchSites = ({ accountId, kind, q, commodity }) =>
  api.get(`${base}/sites/search`, { params: { account_id: accountId, kind, q, commodity } }).then((r) => r.data);

export const getSummary = ({ accountId }) =>
  api.get(`${base}/summary`, { params: { account_id: accountId } }).then((r) => r.data);

export const getDashboard = ({ accountId }) =>
  api.get(`${base}/dashboard`, { params: { account_id: accountId } }).then((r) => r.data);

export const getMarketFeeds = ({ accountId }) =>
  api.get(`${base}/market-feeds`, { params: { account_id: accountId } }).then((r) => r.data);

export const getMarketSeries = ({ accountId, symbol, days = 180 }) =>
  api.get(`${base}/market-feeds/${symbol}/series`, { params: { account_id: accountId, days } }).then((r) => r.data);

export const refreshMarketFeeds = ({ accountId }) =>
  api.post(`${base}/market-feeds/refresh`, null, { params: { account_id: accountId } }).then((r) => r.data);

export const getMarketJob = ({ accountId, jobId }) =>
  api.get(`${base}/market-feeds/jobs/${jobId}`, { params: { account_id: accountId } }).then((r) => r.data);

export const uploadDocument = ({ accountId, id, file, onUploadProgress }) => {
  const fd = new FormData();
  fd.append('file', file);
  return api
    .post(`${base}/contracts/${id}/documents`, fd, {
      params: { account_id: accountId },
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    })
    .then((r) => r.data);
};

export const getDocumentDownloadUrl = ({ accountId, id, docId }) =>
  api.get(`${base}/contracts/${id}/documents/${docId}/download`, { params: { account_id: accountId } }).then((r) => r.data);

export const deleteDocument = ({ accountId, id, docId }) =>
  api.delete(`${base}/contracts/${id}/documents/${docId}`, { params: { account_id: accountId } }).then((r) => r.data);
